import React, { useState, useCallback } from 'react';
import { db } from './firebase';
import { collection, getDocs, getDoc, doc, query, collectionGroup, where } from 'firebase/firestore';

const CLIENTS = [
  { id: '2ejjeh', label: '2ejjeh' },
  { id: 'lucea', label: 'lucea' }
];

function ExportPage({ userRole, userClientId }) {
  const [selectedClient, setSelectedClient] = useState(userClientId || '2ejjeh');
  const [isExporting, setIsExporting] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [errorText, setErrorText] = useState('');
  const [exportSummary, setExportSummary] = useState(null);
  const [testResults, setTestResults] = useState(null);

  const fetchAllCollections = useCallback(async () => {
    const allData = {
      exportDate: new Date().toISOString(),
      collections: {}
    };

    try {
      // List of known top-level collections to export
      const collectionsToExport = ['clients', 'users'];
      let totalDocuments = 0;
      let totalCollections = 0;

      for (const collectionName of collectionsToExport) {
        setProgressText(`Fetching ${collectionName} collection...`);
        
        try {
          const collectionRef = collection(db, collectionName);
          const snapshot = await getDocs(collectionRef);
          
          allData.collections[collectionName] = {
            documents: []
          };

          for (const docSnapshot of snapshot.docs) {
            const docData = {
              id: docSnapshot.id,
              ...docSnapshot.data()
            };

            // If this is the clients collection, also fetch all subcollections
            if (collectionName === 'clients') {
              docData.subcollections = {};
              
              // Known subcollections for clients
              const knownSubcollections = ['imports', 'paid', 'returned'];
              
              for (const subcollectionName of knownSubcollections) {
                try {
                  setProgressText(`Fetching ${collectionName}/${docSnapshot.id}/${subcollectionName}...`);
                  
                  const subcollectionRef = collection(db, 'clients', docSnapshot.id, subcollectionName);
                  const subcollectionSnapshot = await getDocs(subcollectionRef);
                  
                  console.log(`Found ${subcollectionSnapshot.docs.length} documents in ${subcollectionName} for client ${docSnapshot.id}`);
                  
                  docData.subcollections[subcollectionName] = [];
                  
                  // Special handling for imports - use collection group query to get all items
                  if (subcollectionName === 'imports') {
                    console.log(`Using collection group query for imports...`);
                    
                    try {
                      // First, try collection group query
                      const itemsQuery = query(
                        collectionGroup(db, 'items'),
                        where('clientId', '==', docSnapshot.id)
                      );
                      const itemsSnapshot = await getDocs(itemsQuery);
                      
                      console.log(`Collection group query found ${itemsSnapshot.docs.length} items for client ${docSnapshot.id}`);
                      
                      // Initialize items grouping
                      const itemsByImportDate = {};
                      
                      // If no items found, try without the where clause to see all items
                      if (itemsSnapshot.docs.length === 0) {
                        console.log('No items found with clientId filter, trying to get all items...');
                        const allItemsQuery = query(collectionGroup(db, 'items'));
                        const allItemsSnapshot = await getDocs(allItemsQuery);
                        console.log(`Found ${allItemsSnapshot.docs.length} total items in database`);
                        
                        // Log a few sample items to see their structure
                        if (allItemsSnapshot.docs.length > 0) {
                          console.log('Sample item data:', allItemsSnapshot.docs[0].data());
                          console.log('Sample item clientId:', allItemsSnapshot.docs[0].data().clientId);
                        }
                        
                        // Try fallback approach - get import documents directly
                        console.log('Trying fallback approach - getting import documents directly...');
                        try {
                          const importsRef = collection(db, 'clients', docSnapshot.id, 'imports');
                          const importsSnapshot = await getDocs(importsRef);
                          console.log(`Found ${importsSnapshot.docs.length} import documents directly`);
                          
                          // Process each import document
                          for (const importDoc of importsSnapshot.docs) {
                            console.log(`Processing import document ${importDoc.id}`);
                            try {
                              const itemsRef = collection(db, 'clients', docSnapshot.id, 'imports', importDoc.id, 'items');
                              const itemsSnapshot = await getDocs(itemsRef);
                              console.log(`Found ${itemsSnapshot.docs.length} items in ${importDoc.id}`);
                              
                              // Add items to our grouped data
                              for (const itemDoc of itemsSnapshot.docs) {
                                const itemData = {
                                  id: itemDoc.id,
                                  ...itemDoc.data()
                                };
                                
                                const importDate = importDoc.id;
                                if (!itemsByImportDate[importDate]) {
                                  itemsByImportDate[importDate] = [];
                                }
                                itemsByImportDate[importDate].push(itemData);
                              }
                            } catch (itemError) {
                              console.warn(`Could not fetch items for import ${importDoc.id}:`, itemError);
                            }
                          }
                        } catch (importError) {
                          console.warn('Could not fetch import documents directly:', importError);
                        }
                        
                        // If still no items found, try to access known import dates directly
                        if (Object.keys(itemsByImportDate).length === 0) {
                          console.log('No items found through normal methods, trying known import dates...');
                          
                          // Try some common date patterns that might exist
                          const possibleDates = [
                            '21-7-2025', '1-7-2025', '2-7-2025', '3-7-2025', '4-7-2025', '5-7-2025',
                            '6-7-2025', '7-7-2025', '8-7-2025', '9-7-2025', '10-7-2025', '11-7-2025',
                            '12-7-2025', '13-7-2025', '14-7-2025', '15-7-2025', '16-7-2025', '17-7-2025',
                            '18-7-2025', '19-7-2025', '20-7-2025', '22-7-2025', '23-7-2025', '24-7-2025',
                            '25-7-2025', '26-7-2025', '27-7-2025', '28-7-2025', '29-7-2025', '30-7-2025',
                            '31-7-2025'
                          ];
                          
                          for (const date of possibleDates) {
                            try {
                              console.log(`Trying to access items for date ${date}...`);
                              const itemsRef = collection(db, 'clients', docSnapshot.id, 'imports', date, 'items');
                              const itemsSnapshot = await getDocs(itemsRef);
                              
                              if (itemsSnapshot.docs.length > 0) {
                                console.log(`Found ${itemsSnapshot.docs.length} items for date ${date}`);
                                
                                for (const itemDoc of itemsSnapshot.docs) {
                                  const itemData = {
                                    id: itemDoc.id,
                                    ...itemDoc.data()
                                  };
                                  
                                  if (!itemsByImportDate[date]) {
                                    itemsByImportDate[date] = [];
                                  }
                                  itemsByImportDate[date].push(itemData);
                                }
                              }
                            } catch (dateError) {
                              // Silently continue - this date doesn't exist
                            }
                          }
                          
                          console.log(`Found items for ${Object.keys(itemsByImportDate).length} import dates through direct access`);
                        }
                      }
                      
                      // Process items from collection group query
                      for (const itemDoc of itemsSnapshot.docs) {
                        const itemData = {
                          id: itemDoc.id,
                          ...itemDoc.data()
                        };
                        
                        console.log(`Processing item ${itemDoc.id}:`, itemData);
                        
                        const importDate = itemData.importDateKey || 'unknown';
                        if (!itemsByImportDate[importDate]) {
                          itemsByImportDate[importDate] = [];
                        }
                        itemsByImportDate[importDate].push(itemData);
                      }
                      
                      // Convert grouped items to import documents
                      docData.subcollections[subcollectionName] = Object.entries(itemsByImportDate).map(([importDate, items]) => ({
                        id: importDate,
                        importDate: importDate,
                        importId: importDate,
                        isVirtualDocument: true,
                        items: items
                      }));
                      
                      console.log(`Created ${docData.subcollections[subcollectionName].length} import documents from items`);
                      console.log(`Import dates found:`, Object.keys(itemsByImportDate));
                    } catch (itemsError) {
                      console.warn(`Could not fetch items using collection group query:`, itemsError);
                      docData.subcollections[subcollectionName] = [];
                    }
                  } else {
                    // Normal processing for other subcollections
                    for (const subDoc of subcollectionSnapshot.docs) {
                      const subDocData = {
                        id: subDoc.id,
                        ...subDoc.data()
                      };

                      console.log(`Processing ${subcollectionName} document ${subDoc.id}:`, subDocData);
                      docData.subcollections[subcollectionName].push(subDocData);
                    }
                  }
                  
                  console.log(`Final ${subcollectionName} data for ${docSnapshot.id}:`, docData.subcollections[subcollectionName]);
                } catch (subcollectionError) {
                  console.warn(`Could not fetch ${subcollectionName} for client ${docSnapshot.id}:`, subcollectionError);
                  docData.subcollections[subcollectionName] = [];
                }
              }
            }

            allData.collections[collectionName].documents.push(docData);
            totalDocuments++;
          }

          totalCollections++;
        } catch (collectionError) {
          console.warn(`Could not fetch collection ${collectionName}:`, collectionError);
          allData.collections[collectionName] = {
            error: collectionError.message,
            documents: []
          };
        }
      }

      setExportSummary({
        totalCollections,
        totalDocuments,
        exportDate: allData.exportDate,
        collections: Object.keys(allData.collections)
      });

      return allData;
    } catch (error) {
      console.error('Error fetching all collections:', error);
      throw error;
    }
  }, []);

  const fetchClientData = useCallback(async (clientId) => {
    const allData = {
      clientId: clientId,
      exportDate: new Date().toISOString(),
      subcollections: {}
    };

    try {
      // Known subcollections for clients
      const knownSubcollections = ['imports', 'paid', 'returned'];
      let totalDocuments = 0;
      let totalSubcollections = 0;

      for (const subcollectionName of knownSubcollections) {
        try {
          setProgressText(`Fetching ${subcollectionName} for client ${clientId}...`);
          
          if (subcollectionName === 'imports') {
            // Special handling for imports - use collection group query
            try {
              const itemsQuery = query(
                collectionGroup(db, 'items'),
                where('clientId', '==', clientId)
              );
              const itemsSnapshot = await getDocs(itemsQuery);
              
              console.log(`Collection group query found ${itemsSnapshot.docs.length} items for client ${clientId}`);
              
              // Initialize items grouping
              const itemsByImportDate = {};
              
              // If no items found, try fallback approach
              if (itemsSnapshot.docs.length === 0) {
                console.log('No items found with collection group query, trying direct approach...');
                try {
                  const importsRef = collection(db, 'clients', clientId, 'imports');
                  const importsSnapshot = await getDocs(importsRef);
                  console.log(`Found ${importsSnapshot.docs.length} import documents directly`);
                  
                  // Process each import document
                  for (const importDoc of importsSnapshot.docs) {
                    console.log(`Processing import document ${importDoc.id}`);
                    try {
                      const itemsRef = collection(db, 'clients', clientId, 'imports', importDoc.id, 'items');
                      const itemsSnapshot = await getDocs(itemsRef);
                      console.log(`Found ${itemsSnapshot.docs.length} items in ${importDoc.id}`);
                      
                      // Add items to our grouped data
                      for (const itemDoc of itemsSnapshot.docs) {
                        const itemData = {
                          id: itemDoc.id,
                          ...itemDoc.data()
                        };
                        
                        const importDate = importDoc.id;
                        if (!itemsByImportDate[importDate]) {
                          itemsByImportDate[importDate] = [];
                        }
                        itemsByImportDate[importDate].push(itemData);
                      }
                    } catch (itemError) {
                      console.warn(`Could not fetch items for import ${importDoc.id}:`, itemError);
                    }
                  }
                } catch (importError) {
                  console.warn('Could not fetch import documents directly:', importError);
                }
                
                // If still no items found, try to access known import dates directly
                if (Object.keys(itemsByImportDate).length === 0) {
                  console.log('No items found through normal methods, trying known import dates...');
                  
                  // Try some common date patterns that might exist
                  const possibleDates = [
                    '21-7-2025', '1-7-2025', '2-7-2025', '3-7-2025', '4-7-2025', '5-7-2025',
                    '6-7-2025', '7-7-2025', '8-7-2025', '9-7-2025', '10-7-2025', '11-7-2025',
                    '12-7-2025', '13-7-2025', '14-7-2025', '15-7-2025', '16-7-2025', '17-7-2025',
                    '18-7-2025', '19-7-2025', '20-7-2025', '22-7-2025', '23-7-2025', '24-7-2025',
                    '25-7-2025', '26-7-2025', '27-7-2025', '28-7-2025', '29-7-2025', '30-7-2025',
                    '31-7-2025'
                  ];
                  
                  for (const date of possibleDates) {
                    try {
                      console.log(`Trying to access items for date ${date}...`);
                      const itemsRef = collection(db, 'clients', clientId, 'imports', date, 'items');
                      const itemsSnapshot = await getDocs(itemsRef);
                      
                      if (itemsSnapshot.docs.length > 0) {
                        console.log(`Found ${itemsSnapshot.docs.length} items for date ${date}`);
                        
                        for (const itemDoc of itemsSnapshot.docs) {
                          const itemData = {
                            id: itemDoc.id,
                            ...itemDoc.data()
                          };
                          
                          if (!itemsByImportDate[date]) {
                            itemsByImportDate[date] = [];
                          }
                          itemsByImportDate[date].push(itemData);
                        }
                      }
                    } catch (dateError) {
                      // Silently continue - this date doesn't exist
                    }
                  }
                  
                  console.log(`Found items for ${Object.keys(itemsByImportDate).length} import dates through direct access`);
                }
              } else {
                // Process items from collection group query
                for (const itemDoc of itemsSnapshot.docs) {
                  const itemData = {
                    id: itemDoc.id,
                    ...itemDoc.data()
                  };
                  
                  const importDate = itemData.importDateKey || 'unknown';
                  if (!itemsByImportDate[importDate]) {
                    itemsByImportDate[importDate] = [];
                  }
                  itemsByImportDate[importDate].push(itemData);
                }
              }
              
              // Convert grouped items to import documents
              allData.subcollections[subcollectionName] = Object.entries(itemsByImportDate).map(([importDate, items]) => ({
                id: importDate,
                importDate: importDate,
                importId: importDate,
                isVirtualDocument: true,
                items: items
              }));
              
              console.log(`Created ${allData.subcollections[subcollectionName].length} import documents from items`);
              console.log(`Import dates found:`, Object.keys(itemsByImportDate));
              totalDocuments += allData.subcollections[subcollectionName].length;
            } catch (itemsError) {
              console.warn(`Could not fetch items using collection group query:`, itemsError);
              allData.subcollections[subcollectionName] = [];
            }
          } else {
            // Normal processing for other subcollections
            const subcollectionRef = collection(db, 'clients', clientId, subcollectionName);
            const subcollectionSnapshot = await getDocs(subcollectionRef);
            
            console.log(`Found ${subcollectionSnapshot.docs.length} documents in ${subcollectionName} for client ${clientId}`);
            
            allData.subcollections[subcollectionName] = [];
            
            for (const subDoc of subcollectionSnapshot.docs) {
              const subDocData = {
                id: subDoc.id,
                ...subDoc.data()
              };

              console.log(`Processing ${subcollectionName} document ${subDoc.id}:`, subDocData);
              allData.subcollections[subcollectionName].push(subDocData);
              totalDocuments++;
            }
          }
          
          console.log(`Final ${subcollectionName} data for ${clientId}:`, allData.subcollections[subcollectionName]);
          totalSubcollections++;
        } catch (subcollectionError) {
          console.warn(`Could not fetch ${subcollectionName} for client ${clientId}:`, subcollectionError);
          allData.subcollections[subcollectionName] = [];
        }
      }

      setExportSummary({
        totalSubcollections,
        totalDocuments,
        exportDate: allData.exportDate,
        subcollections: Object.keys(allData.subcollections)
      });

      return allData;
    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  }, []);

  const downloadJSON = useCallback((data, filename) => {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleExportAll = useCallback(async () => {
    setIsExporting(true);
    setProgressText('Starting full database export...');
    setErrorText('');
    setExportSummary(null);

    try {
      setProgressText('Fetching all collections from database...');
      const allData = await fetchAllCollections();
      
      setProgressText('Preparing download...');
      const filename = `full_database_export_${new Date().toISOString().split('T')[0]}.json`;
      downloadJSON(allData, filename);
      
      setProgressText('Full database export completed successfully!');
    } catch (error) {
      console.error('Export error:', error);
      setErrorText(error?.message || 'Failed to export data');
      setProgressText('');
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllCollections, downloadJSON]);

  const handleExportClient = useCallback(async () => {
    setIsExporting(true);
    setProgressText('Starting client export...');
    setErrorText('');
    setExportSummary(null);

    try {
      setProgressText('Fetching client data from database...');
      const allData = await fetchClientData(selectedClient);
      
      setProgressText('Preparing download...');
      const filename = `${selectedClient}_export_${new Date().toISOString().split('T')[0]}.json`;
      downloadJSON(allData, filename);
      
      setProgressText('Client export completed successfully!');
    } catch (error) {
      console.error('Export error:', error);
      setErrorText(error?.message || 'Failed to export data');
      setProgressText('');
    } finally {
      setIsExporting(false);
    }
  }, [selectedClient, fetchClientData, downloadJSON]);

  const handleExportFlattened = useCallback(async () => {
    setIsExporting(true);
    setProgressText('Starting flattened export...');
    setErrorText('');
    setExportSummary(null);

    try {
      setProgressText('Fetching client data from database...');
      const allData = await fetchClientData(selectedClient);
      
      // Flatten the data structure - all items in a single array
      const flattenedData = {
        clientId: selectedClient,
        exportDate: new Date().toISOString(),
        totalItems: 0,
        items: []
      };

      // Process imports subcollection for items
      if (allData.subcollections.imports) {
        allData.subcollections.imports.forEach(importData => {
          if (importData.items) {
            importData.items.forEach(item => {
              flattenedData.items.push({
                ...item,
                importId: importData.id,
                importDate: importData.id,
                subcollection: 'imports'
              });
            });
          }
        });
      }

      // Process paid subcollection
      if (allData.subcollections.paid) {
        allData.subcollections.paid.forEach(paidItem => {
          flattenedData.items.push({
            ...paidItem,
            subcollection: 'paid'
          });
        });
      }

      // Process returned subcollection
      if (allData.subcollections.returned) {
        allData.subcollections.returned.forEach(returnedItem => {
          flattenedData.items.push({
            ...returnedItem,
            subcollection: 'returned'
          });
        });
      }

      flattenedData.totalItems = flattenedData.items.length;
      
      setProgressText('Preparing flattened download...');
      const filename = `${selectedClient}_flattened_export_${new Date().toISOString().split('T')[0]}.json`;
      downloadJSON(flattenedData, filename);
      
      setProgressText('Flattened export completed successfully!');
    } catch (error) {
      console.error('Export error:', error);
      setErrorText(error?.message || 'Failed to export data');
      setProgressText('');
    } finally {
      setIsExporting(false);
    }
  }, [selectedClient, fetchClientData, downloadJSON]);

  const handleTestDatabase = useCallback(async () => {
    setIsExporting(true);
    setProgressText('Testing database connection...');
    setErrorText('');
    setTestResults(null);

    try {
      const results = {
        clientId: selectedClient,
        testDate: new Date().toISOString(),
        collections: {}
      };

      // Test clients collection
      setProgressText('Testing clients collection...');
      const clientsRef = collection(db, 'clients');
      const clientsSnapshot = await getDocs(clientsRef);
      results.collections.clients = {
        totalDocs: clientsSnapshot.docs.length,
        documents: clientsSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data()
        }))
      };

      // Test specific client subcollections
      setProgressText(`Testing subcollections for client ${selectedClient}...`);
      const subcollections = ['imports', 'paid', 'returned'];
      
      for (const subName of subcollections) {
        try {
          const subRef = collection(db, 'clients', selectedClient, subName);
          const subSnapshot = await getDocs(subRef);
          
          console.log(`Testing ${subName}: Found ${subSnapshot.docs.length} documents`);
          
          results.collections[subName] = {
            totalDocs: subSnapshot.docs.length,
            documents: subSnapshot.docs.map(doc => ({
              id: doc.id,
              data: doc.data()
            }))
          };

          // For imports, also test items subcollection
          if (subName === 'imports') {
            for (const importDoc of subSnapshot.docs) {
              try {
                const itemsRef = collection(db, 'clients', selectedClient, 'imports', importDoc.id, 'items');
                const itemsSnapshot = await getDocs(itemsRef);
                
                console.log(`Testing imports/${importDoc.id}/items: Found ${itemsSnapshot.docs.length} items`);
                
                if (!results.collections[subName].subcollections) {
                  results.collections[subName].subcollections = {};
                }
                
                results.collections[subName].subcollections[importDoc.id] = {
                  totalItems: itemsSnapshot.docs.length,
                  items: itemsSnapshot.docs.map(item => ({
                    id: item.id,
                    data: item.data()
                  }))
                };
              } catch (itemsError) {
                console.warn(`Could not fetch items for import ${importDoc.id}:`, itemsError);
              }
            }
          }
        } catch (subError) {
          console.warn(`Error testing ${subName}:`, subError);
          results.collections[subName] = {
            error: subError.message,
            totalDocs: 0,
            documents: []
          };
        }
      }

      setTestResults(results);
      setProgressText('Database test completed!');
    } catch (error) {
      console.error('Test error:', error);
      setErrorText(error?.message || 'Failed to test database');
      setProgressText('');
    } finally {
      setIsExporting(false);
    }
  }, [selectedClient]);

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <h2>Export Database to JSON</h2>
      
      <div style={{ marginBottom: 12 }}>
        <label htmlFor="client">Client:&nbsp;</label>
        <select
          id="client"
          value={selectedClient}
          onChange={(e) => setSelectedClient(e.target.value)}
          disabled={isExporting || userRole !== 'admin'}
        >
          {CLIENTS.map(c => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
        {userRole !== 'admin' && (
          <span style={{ marginLeft: '8px', color: '#6c757d', fontSize: '14px' }}>
            (Restricted to your assigned client: {userClientId})
          </span>
        )}
      </div>

      <div style={{ marginBottom: 24 }}>
        <h3>Export Options</h3>
        <p>Choose how you want to export your data:</p>
        
        {userRole === 'admin' && (
          <div style={{ marginBottom: 16 }}>
            <h4>1. Full Database Export (All Collections) - Admin Only</h4>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: 8 }}>
              Exports ALL collections from the database including clients, users, and all subcollections. This is the most comprehensive export.
            </p>
            <button 
              onClick={handleExportAll} 
              disabled={isExporting}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: isExporting ? 'not-allowed' : 'pointer',
                fontSize: '16px'
              }}
            >
              {isExporting ? 'Exporting...' : 'Export Full Database'}
            </button>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <h4>{userRole === 'admin' ? '2' : '1'}. Client-Specific Export (Structured)</h4>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: 8 }}>
            Exports data for the selected client in the same structure as stored in the database, with imports grouped by date.
          </p>
          <button 
            onClick={handleExportClient} 
            disabled={isExporting}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              fontSize: '16px'
            }}
          >
            {isExporting ? 'Exporting...' : 'Export Client Data'}
          </button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h4>{userRole === 'admin' ? '3' : '2'}. Flattened Export (Client Items Only)</h4>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: 8 }}>
            Exports all items for the selected client in a single flat array, easier for analysis in Excel or other tools.
          </p>
          <button 
            onClick={handleExportFlattened} 
            disabled={isExporting}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              fontSize: '16px'
            }}
          >
            {isExporting ? 'Exporting...' : 'Export Flattened Items'}
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 24, padding: 12, background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
        <h4>Debug Database Connection</h4>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: 8 }}>
          Test the database connection to see what data is available before exporting.
        </p>
        <button 
          onClick={handleTestDatabase} 
          disabled={isExporting}
          style={{
            backgroundColor: '#ffc107',
            color: 'black',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: isExporting ? 'not-allowed' : 'pointer',
            fontSize: '14px'
          }}
        >
          {isExporting ? 'Testing...' : 'Test Database Connection'}
        </button>
      </div>

      {progressText ? (
        <div style={{ 
          background: '#f7f7f7', 
          padding: 12, 
          borderRadius: '4px',
          marginBottom: 12,
          border: '1px solid #ddd'
        }}>
          <strong>Progress:</strong> {progressText}
        </div>
      ) : null}

      {errorText ? (
        <div style={{ 
          color: 'crimson', 
          background: '#fff5f5', 
          padding: 12, 
          borderRadius: '4px',
          marginBottom: 12,
          border: '1px solid #f5c6cb'
        }}>
          <strong>Error:</strong> {errorText}
        </div>
      ) : null}

      {exportSummary ? (
        <div style={{ 
          marginTop: 12, 
          padding: 12, 
          background: '#d4edda', 
          border: '1px solid #c3e6cb',
          borderRadius: '4px'
        }}>
          <h4>Export Summary</h4>
          {exportSummary.clientId ? (
            <>
              <div>Client: {exportSummary.clientId}</div>
              <div>Total subcollections: {exportSummary.totalSubcollections}</div>
              <div>Total documents: {exportSummary.totalDocuments}</div>
              <div>Subcollections exported: {exportSummary.subcollections?.join(', ')}</div>
            </>
          ) : (
            <>
              <div>Total collections: {exportSummary.totalCollections}</div>
              <div>Total documents: {exportSummary.totalDocuments}</div>
              <div>Collections exported: {exportSummary.collections?.join(', ')}</div>
            </>
          )}
          <div>Export date: {new Date(exportSummary.exportDate).toLocaleString()}</div>
        </div>
      ) : null}

      {testResults ? (
        <div style={{ 
          marginTop: 12, 
          padding: 12, 
          background: '#d1ecf1', 
          border: '1px solid #bee5eb',
          borderRadius: '4px'
        }}>
          <h4>Database Test Results</h4>
          <div><strong>Client:</strong> {testResults.clientId}</div>
          <div><strong>Test Date:</strong> {new Date(testResults.testDate).toLocaleString()}</div>
          <div style={{ marginTop: 8 }}>
            <strong>Collections Found:</strong>
            {Object.entries(testResults.collections).map(([name, data]) => (
              <div key={name} style={{ marginLeft: 16, marginTop: 4 }}>
                <strong>{name}:</strong> {data.totalDocs} documents
                {data.error && <span style={{ color: 'red' }}> (Error: {data.error})</span>}
                {data.subcollections && (
                  <div style={{ marginLeft: 16 }}>
                    <strong>Items subcollections:</strong>
                    {Object.entries(data.subcollections).map(([importId, items]) => (
                      <div key={importId} style={{ marginLeft: 16 }}>
                        {importId}: {items.totalItems} items
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <details style={{ marginTop: 8 }}>
            <summary>View Raw Data</summary>
            <pre style={{ 
              background: '#f8f9fa', 
              padding: 8, 
              marginTop: 8, 
              fontSize: '12px',
              overflow: 'auto',
              maxHeight: '300px'
            }}>
              {JSON.stringify(testResults, null, 2)}
            </pre>
          </details>
        </div>
      ) : null}

      <div style={{ marginTop: 24, padding: 12, background: '#e9ecef', borderRadius: '4px' }}>
        <h4>Export Format Information</h4>
        <p><strong>Full Database Export:</strong> Exports all collections (clients, users) with complete subcollection data. This is the most comprehensive backup option.</p>
        <p><strong>Client-Specific Export:</strong> Maintains the original database structure with imports grouped by date. Each import contains an array of items.</p>
        <p><strong>Flattened Export:</strong> All items are in a single array with additional fields for importId and importDate for reference.</p>
      </div>
    </div>
  );
}

export default ExportPage;
